import { ref } from 'vue'
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  where,
  limit as firestoreLimit,
  Timestamp,
  onSnapshot,
  writeBatch
} from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useCompanyStore } from '@/stores/company'
import { useAuthStore } from '@/stores/auth'
import type { GeneratedAlert } from '@/types/alertRules'

const isLoading = ref(false)
const error = ref<string | null>(null)

/** Tope por listener (personal y de compañía). Ver setupRealtimeListener. */
const ALERTS_LISTENER_LIMIT = 2000

export function useGeneratedAlerts() {
  const companyStore = useCompanyStore()
  const authStore = useAuthStore()

  function getCollectionRef() {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No hay compañía seleccionada')
    const db = FirebaseProvider.getInstance().getFirestore()
    return collection(db, `Companies/${companyId}/alerts`)
  }

  async function fetchAlerts(options?: {
    onlyUnread?: boolean
    moduleFilter?: string
    severityFilter?: string
    maxResults?: number
  }): Promise<GeneratedAlert[]> {
    try {
      isLoading.value = true
      error.value = null

      const userId = authStore.user?.uid
      if (!userId) return []

      const colRef = getCollectionRef()

      // Personal alerts query
      const personalConstraints: any[] = [
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      ]
      if (options?.onlyUnread) {
        personalConstraints.push(where('read', '==', false))
      }
      if (options?.moduleFilter && options.moduleFilter !== 'all') {
        personalConstraints.push(where('module', '==', options.moduleFilter))
      }
      if (options?.severityFilter && options.severityFilter !== 'all') {
        personalConstraints.push(where('severity', '==', options.severityFilter))
      }
      if (options?.maxResults) {
        personalConstraints.push(firestoreLimit(options.maxResults))
      }

      // Company alerts query
      const companyConstraints: any[] = [
        where('visibility', '==', 'company'),
        orderBy('createdAt', 'desc')
      ]
      if (options?.moduleFilter && options.moduleFilter !== 'all') {
        companyConstraints.push(where('module', '==', options.moduleFilter))
      }
      if (options?.severityFilter && options.severityFilter !== 'all') {
        companyConstraints.push(where('severity', '==', options.severityFilter))
      }
      if (options?.maxResults) {
        companyConstraints.push(firestoreLimit(options.maxResults))
      }

      const [personalSnap, companySnap] = await Promise.all([
        getDocs(query(colRef, ...personalConstraints)),
        getDocs(query(colRef, ...companyConstraints))
      ])

      const personalAlerts = personalSnap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as GeneratedAlert[]

      const companyAlerts = companySnap.docs.map(docSnap => {
        const data = docSnap.data()
        const readByUser = !!(data.readBy && data.readBy[userId])
        return {
          id: docSnap.id,
          ...data,
          read: readByUser,
          readAt: data.readBy?.[userId] ?? null
        }
      }) as GeneratedAlert[]

      // Filter company alerts by read status after normalization
      let filteredCompany = companyAlerts
      if (options?.onlyUnread) {
        filteredCompany = companyAlerts.filter(a => !a.read)
      }

      const merged = [...personalAlerts, ...filteredCompany]
      merged.sort((a, b) => {
        const aTime = a.createdAt && 'toDate' in (a.createdAt as any) ? (a.createdAt as any).toDate().getTime() : new Date(a.createdAt as any).getTime()
        const bTime = b.createdAt && 'toDate' in (b.createdAt as any) ? (b.createdAt as any).toDate().getTime() : new Date(b.createdAt as any).getTime()
        return bTime - aTime
      })

      return merged
    } catch (err) {
      console.error('Error al cargar alertas:', err)
      error.value = err instanceof Error ? err.message : 'Error al cargar alertas'
      return []
    } finally {
      isLoading.value = false
    }
  }

  async function markAsRead(alertId: string, visibility?: 'private' | 'company'): Promise<boolean> {
    try {
      const colRef = getCollectionRef()
      const docRef = doc(colRef, alertId)

      if (visibility === 'company') {
        const userId = authStore.user?.uid
        if (!userId) return false
        await updateDoc(docRef, {
          [`readBy.${userId}`]: Timestamp.now()
        })
      } else {
        await updateDoc(docRef, {
          read: true,
          readAt: Timestamp.now()
        })
      }
      return true
    } catch (err) {
      console.error('Error al marcar alerta como leída:', err)
      return false
    }
  }

  async function markAllAsRead(): Promise<boolean> {
    try {
      const userId = authStore.user?.uid
      if (!userId) return false

      const colRef = getCollectionRef()
      const db = FirebaseProvider.getInstance().getFirestore()
      const batch = writeBatch(db)
      const now = Timestamp.now()

      // Private unread alerts
      const privateQ = query(
        colRef,
        where('userId', '==', userId),
        where('read', '==', false)
      )
      const privateSnap = await getDocs(privateQ)
      privateSnap.docs.forEach(docSnap => {
        batch.update(docSnap.ref, { read: true, readAt: now })
      })

      // Company unread alerts (check readBy for current user)
      const companyQ = query(
        colRef,
        where('visibility', '==', 'company')
      )
      const companySnap = await getDocs(companyQ)
      companySnap.docs.forEach(docSnap => {
        const data = docSnap.data()
        if (!data.readBy || !data.readBy[userId]) {
          batch.update(docSnap.ref, { [`readBy.${userId}`]: now })
        }
      })

      await batch.commit()
      return true
    } catch (err) {
      console.error('Error al marcar todas como leídas:', err)
      return false
    }
  }

  function setupRealtimeListener(callback: (alerts: GeneratedAlert[]) => void) {
    try {
      const userId = authStore.user?.uid
      if (!userId) return () => {}

      const colRef = getCollectionRef()
      // El semáforo de órdenes de compra cuenta severidades sobre las alertas
      // cargadas aquí: las que no lleguen hacen que su orden se vea sin alertas
      // e inflan la tarjeta "Controlado". Tope alto como medida temporal, hasta
      // mover el conteo al servidor.
      const personalQ = query(
        colRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(ALERTS_LISTENER_LIMIT)
      )
      const companyQ = query(
        colRef,
        where('visibility', '==', 'company'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(ALERTS_LISTENER_LIMIT)
      )

      let personalAlerts: GeneratedAlert[] = []
      let companyAlerts: GeneratedAlert[] = []

      function mergeAndEmit() {
        const merged = [...personalAlerts, ...companyAlerts]
        merged.sort((a, b) => {
          const aTime = a.createdAt && 'toDate' in (a.createdAt as any) ? (a.createdAt as any).toDate().getTime() : new Date(a.createdAt as any).getTime()
          const bTime = b.createdAt && 'toDate' in (b.createdAt as any) ? (b.createdAt as any).toDate().getTime() : new Date(b.createdAt as any).getTime()
          return bTime - aTime
        })
        callback(merged)
      }

      const unsub1 = onSnapshot(personalQ, (snapshot) => {
        personalAlerts = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        })) as GeneratedAlert[]
        mergeAndEmit()
      }, (err) => {
        console.error('Error en listener de alertas personales:', err)
        error.value = err.message
      })

      const unsub2 = onSnapshot(companyQ, (snapshot) => {
        companyAlerts = snapshot.docs.map(docSnap => {
          const data = docSnap.data()
          const readByUser = !!(data.readBy && data.readBy[userId])
          return {
            id: docSnap.id,
            ...data,
            read: readByUser,
            readAt: data.readBy?.[userId] ?? null
          }
        }) as GeneratedAlert[]
        mergeAndEmit()
      }, (err) => {
        console.error('Error en listener de alertas de compañía:', err)
        error.value = err.message
      })

      return () => {
        unsub1()
        unsub2()
      }
    } catch (err) {
      console.error('Error al configurar listener de alertas:', err)
      return () => {}
    }
  }

  return {
    isLoading,
    error,
    fetchAlerts,
    markAsRead,
    markAllAsRead,
    setupRealtimeListener
  }
}
